"use client";

import { useEffect, useState } from "react";
import type { PublicQuoteRenterDriver } from "@fm/api";
import {
  REQUIRED_DRIVER_FIELDS,
  type RequiredDriverField,
} from "../../lib/renter-drivers";
import {
  CONTACT_KEYS,
  COUNTRIES,
  DEFAULT_COUNTRY,
  expiryMessage,
  formatDay,
  sentence,
  todayIso,
  type DriverCardStatus,
} from "./draft";
import {
  Field,
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  STATUS_CLASS,
  STATUS_LABEL,
  initials,
} from "./RenterDriverCards";

/** Field errors of one driver, as the server answers them. `_form` is not a field. */
export type QuoteDriverServerErrors = Record<string, string[]>;

export interface QuoteRenterDriverCardsProps {
  /** The drivers staff already put on the quote (endpoint 10). */
  drivers: PublicQuoteRenterDriver[];
  /** The return day (`YYYY-MM-DD`): every license must be valid through it. */
  returnDate: string;
  fleetName?: string;
  /**
   * Saves one driver (endpoint 11). Resolves `null` on success, or the field
   * errors of that driver.
   */
  onSave: (
    id: string,
    values: Record<string, string>,
  ) => Promise<QuoteDriverServerErrors | null>;
  /** Errors from outside a save (the hold creation), keyed by driver id. */
  serverErrors?: Record<string, QuoteDriverServerErrors>;
  /** The renter tried to pay while a driver is not ready. */
  tried?: boolean;
  /** Locked once the payment is set up. */
  disabled?: boolean;
  className?: string;
}

/** What a quote can ask: the required fields, and what an insurer needs (FLEE-561). */
type AskedKey = RequiredDriverField | (typeof CONTACT_KEYS)[number];

const ASKED_KEYS: readonly AskedKey[] = [
  ...REQUIRED_DRIVER_FIELDS,
  ...CONTACT_KEYS,
];

const LICENSE_KEYS: readonly AskedKey[] = [
  "license_number",
  "license_expires_on",
  "license_street",
  "license_city",
  "license_state",
  "license_zipcode",
  "license_country",
];

const LABELS: Record<AskedKey, string> = {
  given_names: "First name(s), as on the license",
  last_name: "Last name, as on the license",
  date_of_birth: "Date of birth",
  license_number: "Driver's license number",
  license_expires_on: "Expiration date",
  license_street: "Street address",
  license_city: "City",
  license_state: "State",
  license_zipcode: "ZIP code",
  license_country: "Country",
  email: "Email",
  phone: "Phone",
};

const INPUT_TYPE: Partial<Record<AskedKey, string>> = {
  date_of_birth: "date",
  license_expires_on: "date",
  email: "email",
  phone: "tel",
};

type Values = Partial<Record<AskedKey, string>>;
type Errors = Partial<Record<AskedKey | "_form", string>>;

/**
 * The keys a driver still owes: the ones the server reports missing, plus the
 * whole license when it does not cover the return.
 */
export function askedKeys(driver: PublicQuoteRenterDriver): AskedKey[] {
  if (driver.ready) return [];

  return ASKED_KEYS.filter(
    (key) =>
      driver.missing.includes(key) ||
      (!driver.license_valid_through_return && LICENSE_KEYS.includes(key)),
  );
}

function toErrors(errors: QuoteDriverServerErrors | undefined): Errors {
  const result: Errors = {};
  if (!errors) return result;

  for (const key of ASKED_KEYS) {
    const message = errors[key]?.[0];
    if (message) result[key] = sentence(message);
  }
  const form = errors._form?.[0];
  if (form) result._form = form;

  return result;
}

function validate(
  keys: AskedKey[],
  values: Values,
  returnDate: string,
): Errors {
  const errors: Errors = {};
  const today = todayIso();

  for (const key of keys) {
    if ((values[key] ?? "").trim() === "") errors[key] = "Required.";
  }

  const expiry = expiryMessage(values.license_expires_on ?? "", returnDate);
  if (keys.includes("license_expires_on") && expiry) {
    errors.license_expires_on = expiry;
  }

  const dob = (values.date_of_birth ?? "").trim();
  if (keys.includes("date_of_birth") && dob && dob.slice(0, 10) >= today) {
    errors.date_of_birth = "Enter a date of birth in the past.";
  }

  return errors;
}

/** Only the asked keys, trimmed, the country upper-cased. */
function toPayload(keys: AskedKey[], values: Values): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const key of keys) {
    const value = (values[key] ?? "").trim();
    payload[key] = key === "license_country" ? value.toUpperCase() : value;
  }
  return payload;
}

function statusOf(
  driver: PublicQuoteRenterDriver,
  keys: AskedKey[],
  values: Values,
): DriverCardStatus {
  if (driver.ready) return "ready";

  const typed = keys.some(
    (key) =>
      (values[key] ?? "").trim() !== "" &&
      !(key === "license_country" && values[key] === DEFAULT_COUNTRY),
  );

  return typed ? "in_progress" : "needed";
}

/**
 * The drivers on the public quote page (FLEE-442 W5). Staff added them: the
 * renter fills only what is missing, one driver at a time, and cannot add or
 * remove anyone. Driver data lives in React state only.
 */
export function QuoteRenterDriverCards({
  drivers,
  returnDate,
  fleetName,
  onSave,
  serverErrors,
  tried = false,
  disabled = false,
  className = "",
}: QuoteRenterDriverCardsProps) {
  const [values, setValues] = useState<Record<string, Values>>({});
  const [errors, setErrors] = useState<Record<string, Errors>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!serverErrors) return;

    setErrors((prev) => {
      const next = { ...prev };
      for (const [id, found] of Object.entries(serverErrors)) {
        next[id] = toErrors(found);
      }
      return next;
    });
  }, [serverErrors]);

  const fleet = fleetName?.trim() || "the fleet";
  const readyCount = drivers.filter((driver) => driver.ready).length;

  const setValue = (id: string, key: AskedKey, value: string) => {
    setValues((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
    setErrors((prev) => {
      if (!prev[id]?.[key]) return prev;
      const { [key]: _dropped, ...rest } = prev[id]!;
      return { ...prev, [id]: rest };
    });
  };

  const save = async (driver: PublicQuoteRenterDriver) => {
    const keys = askedKeys(driver);
    const current = {
      license_country: DEFAULT_COUNTRY,
      ...values[driver.id],
    } as Values;

    const found = validate(keys, current, returnDate);
    if (Object.keys(found).length > 0) {
      setErrors((prev) => ({ ...prev, [driver.id]: found }));
      return;
    }

    setSavingId(driver.id);
    try {
      const failed = await onSave(driver.id, toPayload(keys, current));
      setErrors((prev) => ({ ...prev, [driver.id]: toErrors(failed ?? {}) }));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid="quote-renter-driver-cards"
    >
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Drivers</h2>
        <span className="text-sm text-gray-500">
          {readyCount} of {drivers.length} ready
        </span>
      </div>

      <div aria-hidden="true" className="flex gap-1">
        {drivers.map((driver) => (
          <i
            key={driver.id}
            className={`h-1.5 flex-1 rounded-full ${
              driver.ready ? "bg-green-600" : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <p className="text-sm text-gray-600">
        {fleetName?.trim() || "The fleet"} added these drivers. Finish
        what&apos;s missing before you pay. Each license must be valid through
        the return on {formatDay(returnDate)}.
      </p>

      {drivers.map((driver, index) => {
        const keys = askedKeys(driver);
        const current = values[driver.id] ?? {};
        const status = statusOf(driver, keys, current);
        const cardErrors = errors[driver.id] ?? {};
        const name = [driver.given_names, driver.last_name]
          .map((part) => part.trim())
          .filter(Boolean)
          .join(" ");
        const title = name || (driver.booker ? "Main driver" : "Driver");
        const role =
          driver.role === "main"
            ? driver.booker
              ? "Main driver · you"
              : "Main driver"
            : "Additional driver";
        const needsSave = tried && status !== "ready";

        return (
          <div
            key={driver.id}
            data-testid={`driver-card-${index}`}
            className={`rounded-xl border bg-white ${
              needsSave
                ? "border-destructive"
                : status === "ready"
                  ? "border-green-200"
                  : "border-blue-600 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
            }`}
          >
            <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
              <span
                aria-hidden="true"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  status === "ready"
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {status === "ready" ? "✓" : initials(title)}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="truncate text-sm text-gray-900">{title}</b>
                <small className="text-xs text-gray-500">{role}</small>
              </span>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[status]}`}
              >
                {STATUS_LABEL[status]}
              </span>
            </div>

            <div className="border-t border-gray-100 px-4 py-4">
              {driver.ready ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-700">
                  <span>
                    {driver.license_number_last4
                      ? `License ending ${driver.license_number_last4}`
                      : "License on file"}
                    {driver.license_expires_on
                      ? ` · expires ${formatDay(driver.license_expires_on)}`
                      : ""}
                  </span>
                  <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800">
                    Valid through the return
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                  {!driver.license_valid_through_return &&
                    driver.license_expires_on && (
                      <p className="text-sm text-amber-800">
                        The license on file expires on{" "}
                        {formatDay(driver.license_expires_on)}, before the
                        return. Add the current one.
                      </p>
                    )}

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {keys.map((key) => {
                      const id = `${driver.id}-${key}`;
                      const error = cardErrors[key];
                      const value =
                        current[key] ??
                        (key === "license_country" ? DEFAULT_COUNTRY : "");
                      const wide =
                        key === "license_street" || key === "license_number";

                      return (
                        <Field
                          key={key}
                          id={id}
                          label={LABELS[key]}
                          error={error}
                          span={wide}
                        >
                          {key === "license_country" ? (
                            <select
                              id={id}
                              value={value}
                              disabled={disabled}
                              className={INPUT_CLASS}
                              onChange={(event) =>
                                setValue(driver.id, key, event.target.value)
                              }
                            >
                              {COUNTRIES.map((country) => (
                                <option
                                  key={country.value}
                                  value={country.value}
                                >
                                  {country.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              id={id}
                              type={INPUT_TYPE[key] ?? "text"}
                              value={value}
                              disabled={disabled}
                              autoComplete="off"
                              aria-invalid={!!error}
                              aria-describedby={
                                error ? `${id}-error` : undefined
                              }
                              className={
                                error ? INPUT_ERROR_CLASS : INPUT_CLASS
                              }
                              onChange={(event) =>
                                setValue(driver.id, key, event.target.value)
                              }
                            />
                          )}
                        </Field>
                      );
                    })}
                  </div>

                  {cardErrors._form && (
                    <p role="alert" className="text-sm text-red-600">
                      {cardErrors._form}
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={disabled || savingId === driver.id}
                    onClick={() => void save(driver)}
                    className="w-full rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 sm:w-auto"
                  >
                    Save driver
                  </button>
                </div>
              )}
            </div>

            {needsSave && (
              <p
                role="alert"
                className="border-t border-destructive/20 px-4 py-2.5 text-sm text-red-600"
              >
                Add and save this driver&apos;s license to continue.
              </p>
            )}
          </div>
        );
      })}

      <p className="text-sm text-gray-600">
        Need another driver? Ask {fleet} to add them to the quote.
      </p>
      <p className="text-xs text-gray-500">License numbers are encrypted.</p>
    </div>
  );
}
